/** ***********************************************************************
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ************************************************************************* */

export function setCookie(name: string, value: string, hours?: number) {
	const domain = '.adobe.com';
	const secure = false;
	let expires = '';
	if (hours) {
		const date = new Date();
		date.setTime(date.getTime() + hours * 60 * 60 * 1000);
		expires = `; expires=${date.toUTCString()}`;
	}
	let cookie = `${name}=${value}${expires};domain=${domain}; path=/`;
	if (secure) {
		cookie += '; SameSite=Lax; secure; HttpOnly';
	} else {
		cookie += '; SameSite=None; secure';
	}
	document.cookie = cookie;
}

/* Delete a cookie value */
export function deleteCookie(name: string) {
	document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

/**
 * Set cookie in a specific domain.
 * Set a cookie value (leave hours empty for a session-specific cookie)
 */
export function setCookieWithDomain(
	name: string,
	value: string,
	hours?: number,
	secure?: boolean,
	domain?: string,
) {
	let expires = '';
	let domainStr = '';
	const isSecure =
		secure && !(window.config.env && window.config.env === 'dev')
			? '; secure'
			: '';
	let date: Date;

	if (hours) {
		date = new Date();
		date.setTime(date.getTime() + hours * 60 * 60 * 1000);
		expires = `; expires=${date.toUTCString()}`;
	}
	if (domain) {
		domainStr = `; SameSite=Lax; secure; domain=${domain}`;
	}
	document.cookie = `${name}=${value}${expires}; path=/${isSecure}${domainStr}`;
}

/* Get a cookie value */
export function getCookie(name: string) {
	const nameEQ = `${name}=`;
	const cookieArr = document.cookie.split(';');
	let cookie: string;

	for (let i = cookieArr.length - 1; i >= 0; i--) {
		cookie = cookieArr[i]?.trimStart() ?? '';

		if (cookie.indexOf(nameEQ) === 0) {
			return cookie.substring(nameEQ.length, cookie.length);
		}
	}

	return null;
}
